import React, { useState, useEffect } from 'react';
import './css/BaobabDesignSystem.css';

const ComprehensiveDocumentation = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSubSection, setActiveSubSection] = useState(null);

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Structure complète de la documentation
  const documentationSections = {
    'getting-started': {
      title: 'Guide de démarrage',
      icon: '🚀',
      category: 'Utilisateur',
      subsections: {
        'first-steps': 'Premiers pas',
        'interface-overview': 'Vue d\'ensemble de l\'interface',
        'basic-operations': 'Opérations de base',
        'user-roles': 'Rôles et permissions'
      }
    },
    'dashboard': {
      title: 'Tableau de bord',
      icon: '📊',
      category: 'Utilisateur',
      subsections: {
        'kpis': 'Indicateurs de performance (KPIs)',
        'charts': 'Graphiques et analyses',
        'widgets': 'Widgets et personnalisation',
        'filters': 'Filtres et périodes'
      }
    },
    'opportunities': {
      title: 'Gestion des opportunités',
      icon: '💼',
      category: 'Utilisateur',
      subsections: {
        'create-opportunity': 'Créer une opportunité',
        'pipeline': 'Pipeline commercial',
        'kanban': 'Vue Kanban',
        'conversion': 'Suivi des conversions',
        'comments': 'Commentaires et collaboration'
      }
    },
    'operations': {
      title: 'Module Opérations',
      icon: '🚛',
      category: 'Utilisateur',
      subsections: {
        'create-operation': 'Créer une opération',
        'transport-types': 'Types de transport',
        'incident-management': 'Gestion des incidents',
        'tracking': 'Suivi et statuts',
        'penalties': 'Pénalités d\'immobilisation'
      }
    },
    'documents': {
      title: 'Gestion documentaire',
      icon: '📄',
      category: 'Utilisateur',
      subsections: {
        'document-types': 'Types de documents',
        'pricing': 'Calculs de prix et marges',
        'templates': 'Modèles de documents',
        'pdf-generation': 'Génération PDF',
        'bon-commande': 'Bons de commande'
      }
    },
    'crm': {
      title: 'CRM et Contacts',
      icon: '👥',
      category: 'Utilisateur',
      subsections: {
        'clients': 'Gestion clients',
        'contacts': 'Répertoire contacts',
        'companies': 'Entreprises',
        'import-export': 'Import/Export CSV',
        'segmentation': 'Segmentation clients'
      }
    },
    'transporters': {
      title: 'Transporteurs',
      icon: '🚚',
      category: 'Utilisateur',
      subsections: {
        'transporter-management': 'Gestion des transporteurs',
        'fleet': 'Parc de véhicules',
        'drivers': 'Chauffeurs',
        'rates': 'Tarifications'
      }
    },
    'reports': {
      title: 'Rapports et Export',
      icon: '📈',
      category: 'Utilisateur',
      subsections: {
        'standard-reports': 'Rapports standard',
        'custom-reports': 'Rapports personnalisés',
        'exports': 'Exports de données',
        'analytics': 'Analyses avancées'
      }
    },
    'administration': {
      title: 'Administration',
      icon: '⚙️',
      category: 'Administrateur',
      subsections: {
        'user-management': 'Gestion des utilisateurs',
        'roles-permissions': 'Rôles et permissions',
        'company-settings': 'Paramètres société',
        'system-settings': 'Configuration système',
        'backup-restore': 'Sauvegarde et restauration',
        'monitoring': 'Monitoring système'
      }
    },
    'api-documentation': {
      title: 'Documentation API',
      icon: '🔌',
      category: 'Développeur',
      subsections: {
        'authentication': 'Authentification et tokens',
        'endpoints': 'Endpoints disponibles',
        'rate-limiting': 'Limitations et quotas',
        'webhooks': 'Webhooks et callbacks',
        'sdk': 'SDKs et exemples'
      }
    },
    'security': {
      title: 'Sécurité',
      icon: '🔒',
      category: 'Développeur',
      subsections: {
        'authentication': 'Authentification JWT',
        'authorization': 'Autorisations',
        'data-protection': 'Protection des données',
        'audit-logs': 'Logs d\'audit',
        'best-practices': 'Bonnes pratiques'
      }
    },
    'integration': {
      title: 'Intégrations',
      icon: '🔗',
      category: 'Développeur',
      subsections: {
        'third-party': 'Intégrations tierces',
        'webhooks': 'Configuration webhooks',
        'api-keys': 'Gestion des clés API',
        'external-systems': 'Systèmes externes'
      }
    },
    'troubleshooting': {
      title: 'Dépannage',
      icon: '🔧',
      category: 'Support',
      subsections: {
        'common-issues': 'Problèmes courants',
        'error-codes': 'Codes d\'erreur',
        'performance': 'Problèmes de performance',
        'contact-support': 'Contacter le support'
      }
    }
  };

  const getContent = (sectionKey, subsectionKey) => {
    // Cette fonction retourne le contenu dynamique basé sur la section et sous-section
    const contentMap = {
      'getting-started': {
        'first-steps': <FirstStepsContent />,
        'interface-overview': <InterfaceOverviewContent />,
        'basic-operations': <BasicOperationsContent />,
        'user-roles': <UserRolesContent />
      },
      'dashboard': {
        'kpis': <KPIsContent />,
        'charts': <ChartsContent />,
        'widgets': <WidgetsContent />,
        'filters': <FiltersContent />
      },
      'opportunities': {
        'create-opportunity': <CreateOpportunityContent />,
        'pipeline': <PipelineContent />,
        'kanban': <KanbanContent />,
        'conversion': <ConversionContent />,
        'comments': <CommentsContent />
      },
      'operations': {
        'create-operation': <CreateOperationContent />,
        'transport-types': <TransportTypesContent />,
        'incident-management': <IncidentManagementContent />,
        'tracking': <TrackingContent />,
        'penalties': <PenaltiesContent />
      },
      'documents': {
        'document-types': <DocumentTypesContent />,
        'pricing': <PricingContent />,
        'templates': <TemplatesContent />,
        'pdf-generation': <PDFGenerationContent />,
        'bon-commande': <BonCommandeContent />
      },
      'crm': {
        'clients': <ClientsContent />,
        'contacts': <ContactsContent />,
        'companies': <CompaniesContent />,
        'import-export': <ImportExportContent />,
        'segmentation': <SegmentationContent />
      },
      'transporters': {
        'transporter-management': <TransporterManagementContent />,
        'fleet': <FleetContent />,
        'drivers': <DriversContent />,
        'rates': <RatesContent />
      },
      'reports': {
        'standard-reports': <StandardReportsContent />,
        'custom-reports': <CustomReportsContent />,
        'exports': <ExportsContent />,
        'analytics': <AnalyticsContent />
      },
      'administration': {
        'user-management': <UserManagementContent />,
        'roles-permissions': <RolesPermissionsContent />,
        'company-settings': <CompanySettingsContent />,
        'system-settings': <SystemSettingsContent />,
        'backup-restore': <BackupRestoreContent />,
        'monitoring': <MonitoringContent />
      },
      'api-documentation': {
        'authentication': <APIAuthenticationContent />,
        'endpoints': <APIEndpointsContent />,
        'rate-limiting': <RateLimitingContent />,
        'webhooks': <WebhooksContent />,
        'sdk': <SDKContent />
      },
      'security': {
        'authentication': <SecurityAuthContent />,
        'authorization': <AuthorizationContent />,
        'data-protection': <DataProtectionContent />,
        'audit-logs': <AuditLogsContent />,
        'best-practices': <BestPracticesContent />
      },
      'integration': {
        'third-party': <ThirdPartyContent />,
        'webhooks': <WebhookConfigContent />,
        'api-keys': <APIKeysContent />,
        'external-systems': <ExternalSystemsContent />
      },
      'troubleshooting': {
        'common-issues': <CommonIssuesContent />,
        'error-codes': <ErrorCodesContent />,
        'performance': <PerformanceContent />,
        'contact-support': <ContactSupportContent />
      }
    };

    return contentMap[sectionKey]?.[subsectionKey] || <DefaultContent />;
  };

  const filteredSections = Object.entries(documentationSections).filter(([key, section]) => {
    if (!searchTerm) return true;
    return section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
           section.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
           Object.values(section.subsections).some(subsection => 
             subsection.toLowerCase().includes(searchTerm.toLowerCase())
           );
  });

  // Grouper par catégories
  const categories = ['Utilisateur', 'Administrateur', 'Développeur', 'Support'];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="help-center-modal documentation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="help-center-header">
          <div className="header-title-section">
            <h2>📚 Documentation Baobab Connect</h2>
            <div className="header-version">Version 2.0.0 - Documentation complète</div>
          </div>
          <button 
            className="close-button"
            onClick={onClose}
            title="Fermer (Échap)"
          >
            ×
          </button>
        </div>

        <div className="help-center-content">
          {/* Sidebar navigation */}
          <div className="help-sidebar documentation-sidebar">
            <div className="search-box">
              <input
                type="text"
                placeholder="🔍 Rechercher dans la documentation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="documentation-toc">
              {categories.map(category => {
                const categoryItems = filteredSections.filter(([_, section]) => section.category === category);
                if (categoryItems.length === 0) return null;

                return (
                  <div key={category} className="category-section">
                    <h3 className="category-title">{category}</h3>
                    <nav className="help-navigation">
                      {categoryItems.map(([key, section]) => (
                        <div key={key} className="nav-section">
                          <button
                            className={`nav-item main-nav-item ${activeSection === key ? 'active' : ''}`}
                            onClick={() => {
                              setActiveSection(key);
                              setActiveSubSection(null);
                            }}
                          >
                            <span className="nav-icon">{section.icon}</span>
                            <span className="nav-text">{section.title}</span>
                            <span className="nav-expand">
                              {activeSection === key ? '▼' : '▶'}
                            </span>
                          </button>
                          
                          {activeSection === key && (
                            <div className="subsections">
                              {Object.entries(section.subsections).map(([subKey, subTitle]) => (
                                <button
                                  key={subKey}
                                  className={`nav-item sub-nav-item ${activeSubSection === subKey ? 'active' : ''}`}
                                  onClick={() => setActiveSubSection(subKey)}
                                >
                                  <span className="nav-text">{subTitle}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </nav>
                  </div>
                );
              })}
            </div>

            <div className="help-footer">
              <div className="contact-info">
                <h4>💬 Support technique</h4>
                <p>📧 support@baobabco.net</p>
                <p>📞 +225 05 00 16 37 37</p>
                <p>🌐 www.baobabco.net</p>
                <div className="support-hours">
                  <small>Lun-Ven: 8h-18h GMT</small>
                </div>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="help-main-content documentation-content">
            {activeSection && (
              <div className="documentation-article">
                {activeSubSection ? (
                  <div className="subsection-content">
                    {getContent(activeSection, activeSubSection)}
                  </div>
                ) : (
                  <div className="section-overview">
                    <SectionOverview 
                      section={documentationSections[activeSection]} 
                      onSubsectionClick={setActiveSubSection}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Composants de contenu spécialisés
const SectionOverview = ({ section, onSubsectionClick }) => (
  <div className="help-content section-overview">
    <h1>{section.icon} {section.title}</h1>
    <p className="section-description">
      Sélectionnez un sous-sujet pour accéder à la documentation détaillée :
    </p>
    <div className="subsection-grid">
      {Object.entries(section.subsections).map(([key, title]) => (
        <button
          key={key}
          className="subsection-card"
          onClick={() => onSubsectionClick(key)}
        >
          <h3>{title}</h3>
          <span className="card-arrow">→</span>
        </button>
      ))}
    </div>
  </div>
);

// Premiers pas avec contenu intégré de la FAQ
const FirstStepsContent = () => (
  <div className="help-content">
    <h1>🚀 Premiers pas avec Baobab Connect</h1>
    
    <h2>Bienvenue dans Baobab Connect</h2>
    <p><strong>Baobab Connect</strong> est une plateforme complète de gestion commerciale et logistique spécialement conçue pour les entreprises africaines. Cette solution vous accompagne dans la digitalisation de vos processus métier.</p>
    
    <div className="feature-highlight">
      <h3>🎯 Objectif de la plateforme</h3>
      <p><em>"L'Afrique avance, nous connectons."</em></p>
      <p>Baobab Connect centralise et automatise vos opérations commerciales, logistiques et financières pour optimiser votre performance business.</p>
    </div>
    
    <h2>Configuration initiale</h2>
    
    <h3>1. Première connexion</h3>
    <div className="step-by-step">
      <div className="step">
        <span className="step-number">1</span>
        <div className="step-content">
          <h4>Connexion sécurisée</h4>
          <p>Utilisez vos identifiants fournis par votre administrateur système</p>
          <ul>
            <li>Nom d'utilisateur : fourni par l'admin</li>
            <li>Mot de passe : respecte notre politique de sécurité</li>
            <li>Authentification JWT pour sécurité maximale</li>
          </ul>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">2</span>
        <div className="step-content">
          <h4>Configuration du profil</h4>
          <p>Complétez votre profil utilisateur</p>
          <ul>
            <li>Photo de profil</li>
            <li>Informations de contact</li>
            <li>Préférences d'affichage</li>
          </ul>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">3</span>
        <div className="step-content">
          <h4>Découverte guidée</h4>
          <p>Tour d'horizon des fonctionnalités principales</p>
          <ul>
            <li>Dashboard et KPIs</li>
            <li>Navigation dans les modules</li>
            <li>Système d'aide intégré</li>
          </ul>
        </div>
      </div>
    </div>
    
    <h2>🏠 Fonctionnalités principales</h2>
    <div className="features-grid">
      <ul>
        <li>📊 <strong>Dashboard Intelligent</strong> - 8 tableaux de bord spécialisés avec KPIs temps réel</li>
        <li>🎯 <strong>Gestion Opportunités</strong> - Pipeline commercial avec conversion automatique</li>
        <li>🚛 <strong>Gestion Opérations</strong> - Planification transport avec historique d'activité</li>
        <li>📄 <strong>Documents Commerciaux</strong> - Devis, bons de commande, factures avec gestion paiements intelligente</li>
        <li>🚚 <strong>Flotte Véhicules</strong> - Gestion parc transporteurs avec suivi performance</li>
        <li>👥 <strong>CRM Clients Enrichi</strong> - Base clients avec types de contact et export CSV</li>
        <li>💰 <strong>Gestion Paiements</strong> - Terminologies spécifiques par type de document</li>
        <li>📈 <strong>Objectifs & Performance</strong> - Suivi des résultats avec 8 dashboards</li>
      </ul>
    </div>
    
    <h2>Rôles et permissions</h2>
    <div className="roles-table">
      <table>
        <thead>
          <tr>
            <th>Rôle</th>
            <th>Description</th>
            <th>Accès principal</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Admin</strong></td>
            <td>Administrateur système</td>
            <td>Accès complet à tous les modules</td>
          </tr>
          <tr>
            <td><strong>Manager</strong></td>
            <td>Manager opérationnel</td>
            <td>Supervision équipes et validation</td>
          </tr>
          <tr>
            <td><strong>Commercial</strong></td>
            <td>Responsable commercial</td>
            <td>Opportunités, clients, pipeline</td>
          </tr>
          <tr>
            <td><strong>Opérateur</strong></td>
            <td>Opérateur logistique</td>
            <td>Opérations, documents, suivi</td>
          </tr>
          <tr>
            <td><strong>Recouvrement</strong></td>
            <td>Spécialiste recouvrement</td>
            <td>Suivi paiements, créances</td>
          </tr>
          <tr>
            <td><strong>Comex</strong></td>
            <td>Comité exécutif</td>
            <td>Tableaux de bord, analyses</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <h2>Prochaines étapes</h2>
    <div className="next-steps">
      <h3>Actions recommandées</h3>
      <ol>
        <li><strong>Explorez le dashboard</strong> - Familiarisez-vous avec les KPIs</li>
        <li><strong>Configurez vos données de base</strong> - Clients, transporteurs, contacts</li>
        <li><strong>Créez votre première opportunité</strong> - Testez le pipeline commercial</li>
        <li><strong>Consultez les tutoriels avancés</strong> - Pour une utilisation optimale</li>
      </ol>
    </div>
    
    <div className="support-callout">
      <h3>🆘 Besoin d'aide ?</h3>
      <p>Notre équipe support est disponible pour vous accompagner :</p>
      <ul>
        <li>📧 <strong>Email</strong> : support@baobabco.net</li>
        <li>📞 <strong>Téléphone</strong> : +225 05 00 16 37 37</li>
        <li>⏰ <strong>Horaires</strong> : Lundi à Vendredi, 8h-18h GMT</li>
      </ul>
    </div>
  </div>
);

// Vue d'ensemble de l'interface
const InterfaceOverviewContent = () => (
  <div className="help-content">
    <h1>🖥️ Vue d'ensemble de l'interface</h1>
    
    <h2>Architecture générale</h2>
    <p>L'interface de Baobab Connect est conçue pour une productivité maximale avec une navigation intuitive et des raccourcis efficaces.</p>
    
    <h3>🎨 Design System Baobab</h3>
    <div className="design-system">
      <h4>Couleurs principales</h4>
      <div className="color-palette">
        <div className="color-item">
          <div className="color-box primary-green"></div>
          <span>Vert principal (#22C55E)</span>
        </div>
        <div className="color-item">
          <div className="color-box secondary-green"></div>
          <span>Vert secondaire (#16A34A)</span>
        </div>
        <div className="color-item">
          <div className="color-box orange-accent"></div>
          <span>Orange accent (#FF8C00)</span>
        </div>
      </div>
    </div>
    
    <h2>Composants principaux</h2>
    
    <h3>1. Header (En-tête)</h3>
    <div className="component-desc">
      <h4>Éléments du header :</h4>
      <ul>
        <li><strong>Titre de page</strong> - Contexte actuel</li>
        <li><strong>Barre de recherche globale</strong> - Recherche dans tous les modules</li>
        <li><strong>Centre de notifications</strong> - Alertes et messages système</li>
        <li><strong>Bouton d'aide</strong> - Accès à cette documentation</li>
        <li><strong>Menu utilisateur</strong> - Profil et paramètres</li>
      </ul>
    </div>
    
    <h3>2. Sidebar (Barre latérale)</h3>
    <div className="component-desc">
      <h4>Navigation principale :</h4>
      <ul>
        <li><strong>Dashboard</strong> - Tableau de bord principal</li>
        <li><strong>Opportunités</strong> - Pipeline commercial</li>
        <li><strong>Opérations</strong> - Gestion logistique</li>
        <li><strong>Documents</strong> - Gestion documentaire</li>
        <li><strong>Clients</strong> - CRM intégré</li>
        <li><strong>Contacts</strong> - Répertoire</li>
        <li><strong>Transporteurs</strong> - Partenaires transport</li>
        <li><strong>Rapports</strong> - Analyses et exports</li>
        <li><strong>Paramètres</strong> - Configuration</li>
      </ul>
    </div>
    
    <h3>3. Zone de contenu principal</h3>
    <div className="component-desc">
      <h4>Affichage dynamique :</h4>
      <ul>
        <li><strong>Listes</strong> - Tableaux avec filtres et tri</li>
        <li><strong>Formulaires</strong> - Saisie et modification</li>
        <li><strong>Dashboards</strong> - Graphiques et widgets</li>
        <li><strong>Modales</strong> - Actions contextuelles</li>
      </ul>
    </div>
    
    <h2>Raccourcis clavier</h2>
    <div className="shortcuts-table">
      <table>
        <thead>
          <tr>
            <th>Raccourci</th>
            <th>Action</th>
            <th>Contexte</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>Ctrl + K</code></td>
            <td>Recherche globale</td>
            <td>Partout</td>
          </tr>
          <tr>
            <td><code>Échap</code></td>
            <td>Fermer modale/sidebar</td>
            <td>Modales ouvertes</td>
          </tr>
          <tr>
            <td><code>F1</code></td>
            <td>Ouvrir l'aide</td>
            <td>Partout</td>
          </tr>
          <tr>
            <td><code>Alt + [1-9]</code></td>
            <td>Navigation rapide menu</td>
            <td>Sidebar</td>
          </tr>
          <tr>
            <td><code>Ctrl + S</code></td>
            <td>Sauvegarder formulaire</td>
            <td>Formulaires</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <h2>Interface responsive</h2>
    <div className="responsive-info">
      <h3>📱 Adaptation mobile</h3>
      <ul>
        <li><strong>Sidebar collapse</strong> - Navigation hamburger sur mobile</li>
        <li><strong>Tableaux adaptatifs</strong> - Défilement horizontal</li>
        <li><strong>Formulaires optimisés</strong> - Saisie tactile</li>
        <li><strong>Graphiques redimensionnables</strong> - Lisibilité conservée</li>
      </ul>
      
      <h3>💻 Points de rupture</h3>
      <ul>
        <li><strong>Mobile</strong> : &lt; 768px</li>
        <li><strong>Tablette</strong> : 768px - 1024px</li>
        <li><strong>Desktop</strong> : &gt; 1024px</li>
      </ul>
    </div>
    
    <h2>Personnalisation de l'interface</h2>
    <div className="customization">
      <h3>🎛️ Options disponibles</h3>
      <ul>
        <li><strong>Thème</strong> - Mode clair/sombre (si configuré)</li>
        <li><strong>Langue</strong> - Français par défaut</li>
        <li><strong>Widgets dashboard</strong> - Personnalisation des KPIs</li>
        <li><strong>Colonnes tableaux</strong> - Masquer/afficher colonnes</li>
      </ul>
    </div>
    
    <div className="tips-callout">
      <h3>💡 Conseils d'utilisation</h3>
      <ul>
        <li>Utilisez la recherche globale pour accéder rapidement à vos données</li>
        <li>Personnalisez votre dashboard pour suivre vos KPIs prioritaires</li>
        <li>Exploitez les filtres avancés pour optimiser vos analyses</li>
        <li>Configurez les notifications pour rester informé en temps réel</li>
      </ul>
    </div>
  </div>
);

// Les autres composants de contenu - implémentés avec du contenu réel
const BasicOperationsContent = () => (
  <div className="help-content">
    <h1>🎯 Opérations de base</h1>
    
    <h2>Vue d'ensemble</h2>
    <p>Les opérations de base constituent le fondement de votre utilisation quotidienne de Baobab Connect. Maîtriser ces opérations vous permettra d'être efficace dès les premiers jours.</p>
    
    <h2>🔄 Workflow principal recommandé</h2>
    
    <div className="step-by-step">
      <div className="step">
        <span className="step-number">1</span>
        <div className="step-content">
          <h4>🎯 Créer une opportunité</h4>
          <p>Commencez toujours par identifier et enregistrer une opportunité commerciale</p>
          <ul>
            <li>Rendez-vous dans le module "Opportunités"</li>
            <li>Cliquez sur "+ Nouvelle opportunité"</li>
            <li>Renseignez le client, la valeur estimée et la description</li>
            <li>Assignez un commercial responsable</li>
          </ul>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">2</span>
        <div className="step-content">
          <h4>📋 Créer un devis</h4>
          <p>Formalisez votre proposition commerciale</p>
          <ul>
            <li>Accédez au module "Documents"</li>
            <li>Sélectionnez "Devis" comme type de document</li>
            <li>Liez le devis à l'opportunité créée</li>
            <li>Définissez la tarification avec marge</li>
            <li>Générez et envoyez le PDF au client</li>
          </ul>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">3</span>
        <div className="step-content">
          <h4>✅ Validation client</h4>
          <p>Une fois le devis accepté par le client</p>
          <ul>
            <li>Changez le statut de l'opportunité à "Gagnée"</li>
            <li>Modifiez le statut du devis à "Accepté"</li>
            <li>Générez automatiquement un bon de commande</li>
          </ul>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">4</span>
        <div className="step-content">
          <h4>🚛 Planifier l'opération</h4>
          <p>Organisez l'exécution logistique</p>
          <ul>
            <li>Créez l'opération depuis l'opportunité gagnée</li>
            <li>Assignez un transporteur et véhicule</li>
            <li>Définissez l'itinéraire et les horaires</li>
            <li>Lancez l'exécution</li>
          </ul>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">5</span>
        <div className="step-content">
          <h4>📄 Finaliser la facturation</h4>
          <p>Terminez le cycle commercial</p>
          <ul>
            <li>Une fois l'opération terminée, générez la facture</li>
            <li>Envoyez la facture au client</li>
            <li>Suivez les paiements dans le module Documents</li>
            <li>Mettez à jour les statuts de paiement</li>
          </ul>
        </div>
      </div>
    </div>
    
    <h2>📊 Actions courantes</h2>
    
    <h3>Navigation rapide</h3>
    <ul>
      <li><strong>Recherche globale</strong> : Utilisez Ctrl+K pour rechercher dans tous les modules</li>
      <li><strong>Navigation sidebar</strong> : Utilisez Alt+[1-9] pour accéder rapidement aux modules</li>
      <li><strong>Actions contextuelles</strong> : Clic droit sur les éléments pour les actions rapides</li>
    </ul>
    
    <h3>Gestion des filtres</h3>
    <ul>
      <li><strong>Filtres rapides</strong> : Utilisez les boutons de filtre en haut des listes</li>
      <li><strong>Recherche avancée</strong> : Combinez plusieurs critères de recherche</li>
      <li><strong>Sauvegarde de vues</strong> : Enregistrez vos filtres les plus utilisés</li>
    </ul>
    
    <h3>Export et partage</h3>
    <ul>
      <li><strong>Export CSV</strong> : Disponible dans tous les modules de liste</li>
      <li><strong>Generation PDF</strong> : Pour tous les documents commerciaux</li>
      <li><strong>Impression</strong> : Optimisée pour tous les formats de document</li>
    </ul>
    
    <h2>🏆 Bonnes pratiques</h2>
    
    <div className="best-practices">
      <h3>✅ Recommandations</h3>
      <ul>
        <li><strong>Cohérence des données</strong> : Toujours lier les opportunités, opérations et documents</li>
        <li><strong>Mise à jour régulière</strong> : Actualisez les statuts en temps réel</li>
        <li><strong>Documentation</strong> : Utilisez les champs de commentaires et notes</li>
        <li><strong>Sauvegarde</strong> : Sauvegardez régulièrement votre travail</li>
      </ul>
      
      <h3>À éviter</h3>
      <ul>
        <li>Ne pas lier les documents aux opportunités</li>
        <li>Oublier de mettre à jour les statuts</li>
        <li>Créer des données en double</li>
        <li>Ignorer les alertes système</li>
      </ul>
    </div>
    
    <h2>🔧 Raccourcis utiles</h2>
    
    <div className="shortcuts-table">
      <table>
        <thead>
          <tr>
            <th>Action</th>
            <th>Raccourci</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Recherche globale</td>
            <td><code>Ctrl + K</code></td>
            <td>Recherche dans tous les modules</td>
          </tr>
          <tr>
            <td>Nouveau document</td>
            <td><code>Ctrl + N</code></td>
            <td>Créer un nouveau document</td>
          </tr>
          <tr>
            <td>Sauvegarder</td>
            <td><code>Ctrl + S</code></td>
            <td>Sauvegarder le formulaire actuel</td>
          </tr>
          <tr>
            <td>Fermer modal</td>
            <td><code>Échap</code></td>
            <td>Fermer la modale ouverte</td>
          </tr>
          <tr>
            <td>Aide</td>
            <td><code>F1</code></td>
            <td>Ouvrir cette documentation</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

// Rôles utilisateur - intégré depuis la FAQ
const UserRolesContent = () => (
  <div className="help-content">
    <h1>👥 Rôles et permissions</h1>
    
    <h2>Système de rôles</h2>
    <p>Baobab Connect propose un système de rôles flexible qui s'adapte à l'organisation de votre entreprise. Chaque rôle dispose d'autorisations spécifiques pour assurer la sécurité et l'efficacité.</p>
    
    <h2>🏢 Rôles disponibles</h2>
    
    <div className="roles-overview">
      <div className="role-card admin">
        <h3>👑 Administrateur</h3>
        <div className="role-details">
          <h4>Responsabilités</h4>
          <p>Gestion complète du système et supervision générale</p>
          
          <h4>Accès complet à</h4>
          <ul>
            <li>Tous les modules sans restriction</li>
            <li>Gestion des utilisateurs</li>
            <li>Configuration système</li>
            <li>Sauvegarde et restauration</li>
            <li>Monitoring et logs système</li>
          </ul>
          
          <h4>Actions spéciales</h4>
          <ul>
            <li>Créer/modifier/supprimer des utilisateurs</li>
            <li>Modifier les paramètres globaux</li>
            <li>Accès aux logs d'audit</li>
            <li>Export de toutes les données</li>
          </ul>
        </div>
      </div>
      
      <div className="role-card manager">
        <h3>💼 Manager</h3>
        <div className="role-details">
          <h4>Responsabilités</h4>
          <p>Supervision d'équipe et validation des opérations importantes</p>
          
          <h4>Accès principal à</h4>
          <ul>
            <li>Dashboard avancé avec tous les KPIs</li>
            <li>Gestion des opportunités de son équipe</li>
            <li>Validation des devis importants</li>
            <li>Rapports et analyses</li>
            <li>Objectifs et performance équipe</li>
          </ul>
          
          <h4>Limitations</h4>
          <ul>
            <li>Pas d'accès à la configuration système</li>
            <li>Pas de gestion des utilisateurs</li>
            <li>Données limitées à son périmètre</li>
          </ul>
        </div>
      </div>
      
      <div className="role-card commercial">
        <h3>🎯 Commercial</h3>
        <div className="role-details">
          <h4>Responsabilités</h4>
          <p>Développement commercial et gestion de la relation client</p>
          
          <h4>Accès principal à</h4>
          <ul>
            <li>Module Opportunités (création, suivi, conversion)</li>
            <li>CRM Clients (ses clients assignés)</li>
            <li>Documents commerciaux (devis, factures)</li>
            <li>Pipeline commercial et KPIs commerciaux</li>
            <li>Objectifs personnels</li>
          </ul>
          
          <h4>Permissions spécifiques</h4>
          <ul>
            <li>Création de devis jusqu'à un montant limite</li>
            <li>Modification de ses opportunités</li>
            <li>Export de ses données clients</li>
            <li>Accès aux rapports commerciaux</li>
          </ul>
        </div>
      </div>
      
      <div className="role-card operateur">
        <h3>🚛 Opérateur</h3>
        <div className="role-details">
          <h4>Responsabilités</h4>
          <p>Gestion des opérations logistiques et suivi terrain</p>
          
          <h4>Accès principal à</h4>
          <ul>
            <li>Module Opérations (création, suivi, clôture)</li>
            <li>Gestion des transporteurs et véhicules</li>
            <li>Suivi des livraisons</li>
            <li>Gestion des incidents</li>
            <li>Documents opérationnels</li>
          </ul>
          
          <h4>Actions spécialisées</h4>
          <ul>
            <li>Création et modification d'opérations</li>
            <li>Assignation de transporteurs</li>
            <li>Mise à jour des statuts de livraison</li>
            <li>Signalement d'incidents</li>
          </ul>
        </div>
      </div>
      
      <div className="role-card recouvrement">
        <h3>💰 Opérateur Recouvrement</h3>
        <div className="role-details">
          <h4>Responsabilités</h4>
          <p>Suivi des paiements et gestion de la trésorerie</p>
          
          <h4>Accès principal à</h4>
          <ul>
            <li>Module Documents (focus paiements)</li>
            <li>Suivi des factures et encaissements</li>
            <li>Relances clients</li>
            <li>Rapports financiers</li>
            <li>Créances et impayés</li>
          </ul>
          
          <h4>Fonctions spécialisées</h4>
          <ul>
            <li>Enregistrement des paiements</li>
            <li>Génération de rappels</li>
            <li>Suivi des délais de paiement</li>
            <li>Analyses de trésorerie</li>
          </ul>
        </div>
      </div>
      
      <div className="role-card comex">
        <h3>📊 Comex</h3>
        <div className="role-details">
          <h4>Responsabilités</h4>
          <p>Pilotage stratégique et analyse de performance</p>
          
          <h4>Accès principal à</h4>
          <ul>
            <li>Dashboards exécutifs</li>
            <li>Rapports consolidés</li>
            <li>KPIs stratégiques</li>
            <li>Analyses prédictives</li>
            <li>Objectifs généraux</li>
          </ul>
          
          <h4>Vues spécialisées</h4>
          <ul>
            <li>Vue d'ensemble globale (lecture seule)</li>
            <li>Analyses de rentabilité</li>
            <li>Tableaux de bord personnalisés</li>
            <li>Exports pour le reporting</li>
          </ul>
        </div>
      </div>
    </div>
    
    <h2>🔐 Matrice des permissions</h2>
    
    <div className="permissions-matrix">
      <table>
        <thead>
          <tr>
            <th>Module/Action</th>
            <th>Admin</th>
            <th>Manager</th>
            <th>Commercial</th>
            <th>Opérateur</th>
            <th>Recouvrement</th>
            <th>Comex</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Dashboard complet</td>
            <td>✅</td>
            <td>✅</td>
            <td>⚡ Partiel</td>
            <td>⚡ Partiel</td>
            <td>⚡ Partiel</td>
            <td>✅</td>
          </tr>
          <tr>
            <td>Opportunités (Toutes)</td>
            <td>✅</td>
            <td>✅</td>
            <td>⚡ Assignées</td>
            <td>👁️ Lecture</td>
            <td>👁️ Lecture</td>
            <td>👁️ Lecture</td>
          </tr>
          <tr>
            <td>Opérations (Toutes)</td>
            <td>✅</td>
            <td>✅</td>
            <td>👁️ Lecture</td>
            <td>✅</td>
            <td>👁️ Lecture</td>
            <td>👁️ Lecture</td>
          </tr>
          <tr>
            <td>Documents (Tous)</td>
            <td>✅</td>
            <td>✅</td>
            <td>⚡ Ses clients</td>
            <td>⚡ Ses opérations</td>
            <td>✅</td>
            <td>👁️ Lecture</td>
          </tr>
          <tr>
            <td>CRM Clients</td>
            <td>✅</td>
            <td>✅</td>
            <td>⚡ Assignés</td>
            <td>👁️ Lecture</td>
            <td>👁️ Lecture</td>
            <td>👁️ Lecture</td>
          </tr>
          <tr>
            <td>Transporteurs</td>
            <td>✅</td>
            <td>✅</td>
            <td>👁️ Lecture</td>
            <td>✅</td>
            <td>👁️ Lecture</td>
            <td>👁️ Lecture</td>
          </tr>
          <tr>
            <td>Utilisateurs</td>
            <td>✅</td>
            <td>❌</td>
            <td>❌</td>
            <td>❌</td>
            <td>❌</td>
            <td>❌</td>
          </tr>
          <tr>
            <td>Paramètres système</td>
            <td>✅</td>
            <td>❌</td>
            <td>❌</td>
            <td>❌</td>
            <td>❌</td>
            <td>❌</td>
          </tr>
        </tbody>
      </table>
      
      <div className="legend">
        <p><strong>Légende :</strong></p>
        <ul>
          <li>✅ Accès complet (Créer, Lire, Modifier, Supprimer)</li>
          <li>👁️ Lecture seule</li>
          <li>⚡ Accès partiel (données assignées uniquement)</li>
          <li>❌ Pas d'accès</li>
        </ul>
      </div>
    </div>
    
    <h2>🔧 Gestion des rôles</h2>
    
    <h3>Attribution des rôles</h3>
    <p>Seuls les administrateurs peuvent modifier les rôles des utilisateurs :</p>
    <ol>
      <li>Accédez aux <strong>Paramètres → Gestion des utilisateurs</strong></li>
      <li>Sélectionnez l'utilisateur à modifier</li>
      <li>Changez le rôle dans le menu déroulant</li>
      <li>Sauvegardez les modifications</li>
    </ol>
    
    <h3>Sécurité et bonnes pratiques</h3>
    <ul>
      <li><strong>Principe du moindre privilège</strong> : Accordez uniquement les permissions nécessaires</li>
      <li><strong>Révision régulière</strong> : Vérifiez périodiquement les attributions de rôles</li>
      <li><strong>Traçabilité</strong> : Toutes les modifications sont loggées</li>
      <li><strong>Formation</strong> : Assurez-vous que chaque utilisateur comprend son rôle</li>
    </ul>
    
    <div className="security-note">
      <h3>🛡️ Note de sécurité</h3>
      <p>Les changements de rôles prennent effet immédiatement. L'utilisateur devra se reconnecter pour que toutes les permissions soient mises à jour dans son interface.</p>
    </div>
  </div>
);

// Contenu par défaut pour les composants non encore implémentés
const DefaultContent = () => (
  <div className="help-content">
    <h1>🚧 Contenu en cours de rédaction</h1>
    <p>Cette section de la documentation est en cours de préparation.</p>
    <p>Revenez bientôt pour plus d'informations détaillées.</p>
    
    <div className="coming-soon">
      <h3>📋 À venir prochainement :</h3>
      <ul>
        <li>Guides détaillés étape par étape</li>
        <li>Captures d'écran illustratives</li>
        <li>Exemples pratiques</li>
        <li>Conseils et bonnes pratiques</li>
        <li>FAQ spécialisée</li>
      </ul>
    </div>
    
    <div className="help-resources">
      <h3>💬 En attendant, vous pouvez :</h3>
      <ul>
        <li>📧 Contacter le support : support@baobabco.net</li>
        <li>📞 Appeler l'assistance : +225 05 00 16 37 37</li>
        <li>📚 Consulter les autres sections disponibles</li>
        <li>💡 Suggérer des améliorations</li>
      </ul>
    </div>
  </div>
);

// KPIs et indicateurs - intégré depuis la FAQ et guide administration
const KPIsContent = () => (
  <div className="help-content">
    <h1>📊 Indicateurs de performance (KPIs)</h1>
    
    <h2>Vue d'ensemble</h2>
    <p>Les KPIs (Key Performance Indicators) de Baobab Connect vous donnent une vision claire et en temps réel de la performance de votre entreprise logistique. Ils sont calculés automatiquement et mis à jour en continu.</p>
    
    <h2>💰 KPIs Financiers</h2>
    
    <div className="kpi-section">
      <h3>Chiffre d'Affaires Cumulé</h3>
      <div className="kpi-details">
        <h4>📋 Définition</h4>
        <p>Somme de tous les revenus générés par les factures payées ou partiellement payées</p>
        
        <h4>🧮 Formule de calcul</h4>
        <div className="formula">
          <code>CA Cumulé = Σ(total_ttc des factures avec statut "payée" ou "partiellement_payé")</code>
        </div>
        
        <h4>📈 Interprétation</h4>
        <ul>
          <li><strong>Tendance croissante</strong> : Bonne santé commerciale</li>
          <li><strong>Stagnation</strong> : Nécessite des actions commerciales</li>
          <li><strong>Évolution mensuelle</strong> : Comparaison avec le mois précédent</li>
        </ul>
        
        <h4>🎯 Actions recommandées</h4>
        <ul>
          <li>Si croissance &lt; 5% : Intensifier prospection</li>
          <li>Si décroissance : Analyser les causes et ajuster la stratégie</li>
          <li>Objectif recommandé : +10% par mois</li>
        </ul>
      </div>
    </div>
    
    <div className="kpi-section">
      <h3>Bénéfice Cumulé</h3>
      <div className="kpi-details">
        <h4>📋 Définition</h4>
        <p>Profit net généré par les marges commerciales et les frais d'immobilisation</p>
        
        <h4>🧮 Formule de calcul</h4>
        <div className="formula">
          <code>Bénéfice = Σ(marge_ht) + Σ(frais_immobilisation_camion)</code>
        </div>
        
        <h4>💡 Composition</h4>
        <ul>
          <li><strong>Marges HT</strong> : Différence entre tarif client et tarif transporteur</li>
          <li><strong>Frais d'immobilisation</strong> : Pénalités facturées pour retards</li>
        </ul>
      </div>
    </div>
    
    <div className="kpi-section">
      <h3>Marge Moyenne (%)</h3>
      <div className="kpi-details">
        <h4>📋 Définition</h4>
        <p>Pourcentage moyen de marge sur l'ensemble des opérations payées</p>
        
        <h4>🧮 Formule de calcul</h4>
        <div className="formula">
          <code>Marge Moyenne = (Total des marges HT ÷ Total CA HT) × 100</code>
        </div>
        
        <h4>📊 Benchmarks</h4>
        <ul>
          <li><strong>&gt; 25%</strong> : Excellente marge</li>
          <li><strong>15-25%</strong> : Marge correcte</li>
          <li><strong>&lt; 15%</strong> : Marge faible, à améliorer</li>
        </ul>
      </div>
    </div>
    
    <div className="kpi-section">
      <h3>CA Prévisionnel</h3>
      <div className="kpi-details">
        <h4>📋 Définition</h4>
        <p>Chiffre d'affaires réalisable à court terme (factures émises en attente de paiement)</p>
        
        <h4>🧮 Formule de calcul</h4>
        <div className="formula">
          <code>CA Prévisionnel = Σ(total_ht des factures avec statut "en_attente")</code>
        </div>
        
        <h4>📈 Utilité</h4>
        <ul>
          <li>Prévision de trésorerie à court terme</li>
          <li>Planification financière</li>
          <li>Suivi des encaissements attendus</li>
        </ul>
      </div>
    </div>
    
    <h2>🎯 KPIs Commerciaux</h2>
    
    <div className="kpi-section">
      <h3>Taux de Conversion</h3>
      <div className="kpi-details">
        <h4>📋 Définition</h4>
        <p>Pourcentage d'opportunités transformées en affaires gagnées</p>
        
        <h4>🧮 Formule de calcul</h4>
        <div className="formula">
          <code>Taux de Conversion = (Opportunités gagnées ÷ Total opportunités) × 100</code>
        </div>
        
        <h4>📊 Benchmarks sectoriels</h4>
        <ul>
          <li><strong>&gt; 30%</strong> : Excellent taux</li>
          <li><strong>20-30%</strong> : Taux correct</li>
          <li><strong>&lt; 20%</strong> : À améliorer</li>
        </ul>
      </div>
    </div>
    
    <div className="kpi-section">
      <h3>Pipeline Value</h3>
      <div className="kpi-details">
        <h4>📋 Définition</h4>
        <p>Valeur totale des opportunités en cours de négociation</p>
        
        <h4>🧮 Formule de calcul</h4>
        <div className="formula">
          <code>Pipeline Value = Σ(valeur_estimée des opportunités actives)</code>
        </div>
        
        <h4>📈 Statuts inclus</h4>
        <ul>
          <li>Prospection</li>
          <li>Qualification</li>
          <li>Proposition</li>
          <li>Négociation</li>
        </ul>
      </div>
    </div>
    
    <h2>🚛 KPIs Opérationnels</h2>
    
    <div className="kpi-section">
      <h3>Taux de Ponctualité</h3>
      <div className="kpi-details">
        <h4>📋 Définition</h4>
        <p>Pourcentage d'opérations livrées dans les délais prévus</p>
        
        <h4>🧮 Formule de calcul</h4>
        <div className="formula">
          <code>Taux Ponctualité = (Opérations à l'heure ÷ Total opérations terminées) × 100</code>
        </div>
        
        <h4>🎯 Objectifs qualité</h4>
        <ul>
          <li><strong>&gt; 95%</strong> : Excellence opérationnelle</li>
          <li><strong>85-95%</strong> : Niveau satisfaisant</li>
          <li><strong>&lt; 85%</strong> : Amélioration nécessaire</li>
        </ul>
      </div>
    </div>
    
    <div className="kpi-section">
      <h3>Taux d'Incidents</h3>
      <div className="kpi-details">
        <h4>📋 Définition</h4>
        <p>Pourcentage d'opérations ayant rencontré des incidents</p>
        
        <h4>📊 Types d'incidents suivis</h4>
        <ul>
          <li>Pannes véhicules</li>
          <li>Retards de livraison</li>
          <li>Problèmes d'accès</li>
          <li>Contrôles routiers</li>
          <li>Conditions météorologiques</li>
        </ul>
      </div>
    </div>
    
    <h2>💼 KPIs par Commercial</h2>
    
    <div className="kpi-section">
      <h3>CA par Commercial</h3>
      <div className="kpi-details">
        <h4>📋 Définition</h4>
        <p>Chiffre d'affaires généré par chaque commercial</p>
        
        <h4>📈 Métriques détaillées</h4>
        <ul>
          <li>CA mensuel individuel</li>
          <li>Évolution vs mois précédent</li>
          <li>Atteinte des objectifs</li>
          <li>Nombre de nouveaux clients</li>
          <li>Taux de conversion personnel</li>
        </ul>
      </div>
    </div>
    
    <h2>🎛️ Configuration des KPIs</h2>
    
    <h3>Personnalisation des seuils</h3>
    <p>Les administrateurs peuvent configurer :</p>
    <ul>
      <li><strong>Seuils d'alerte</strong> : Niveaux déclenchant des notifications</li>
      <li><strong>Objectifs par période</strong> : Cibles mensuelles/trimestrielles</li>
      <li><strong>Benchmarks sectoriels</strong> : Références métier</li>
      <li><strong>Alertes automatiques</strong> : Notifications en cas de déviation</li>
    </ul>
    
    <h3>Rapports automatisés</h3>
    <ul>
      <li><strong>Reporting quotidien</strong> : KPIs envoyés par email</li>
      <li><strong>Synthèse hebdomadaire</strong> : Analyse des tendances</li>
      <li><strong>Rapport mensuel</strong> : Bilan complet de performance</li>
    </ul>
    
    <h2>📈 Analyse des tendances</h2>
    
    <h3>Indicateurs à surveiller</h3>
    <ul>
      <li><strong>Évolutions brutales</strong> : Variations de +/- 20% en 1 mois</li>
      <li><strong>Tendances saisonnières</strong> : Cycles récurrents</li>
      <li><strong>Corrélations</strong> : Relations entre différents KPIs</li>
      <li><strong>Points d'inflexion</strong> : Changements de tendance</li>
    </ul>
    
    <div className="analysis-tips">
      <h3>💡 Conseils d'analyse</h3>
      <ul>
        <li><strong>Vision globale</strong> : Ne pas se focaliser sur un seul KPI</li>
        <li><strong>Contexte temporel</strong> : Comparer sur des périodes similaires</li>
        <li><strong>Facteurs externes</strong> : Prendre en compte les événements exceptionnels</li>
        <li><strong>Actions correctives</strong> : Définir des plans d'action en cas de déviation</li>
      </ul>
    </div>
  </div>
);
const ChartsContent = () => <DefaultContent />;
const WidgetsContent = () => <DefaultContent />;
const FiltersContent = () => <DefaultContent />;
const CreateOpportunityContent = () => <DefaultContent />;
const PipelineContent = () => <DefaultContent />;
const KanbanContent = () => <DefaultContent />;
const ConversionContent = () => <DefaultContent />;
const CommentsContent = () => <DefaultContent />;
const CreateOperationContent = () => <DefaultContent />;
const TransportTypesContent = () => <DefaultContent />;
// Gestion des incidents - intégré depuis GUIDE_INCIDENTS_RETARDS.md
const IncidentManagementContent = () => (
  <div className="help-content">
    <h1>Gestion des incidents</h1>
    
    <h2>🚨 Vue d'ensemble</h2>
    <p>Le système de gestion des incidents de Baobab Connect permet un suivi en temps réel des problèmes opérationnels et l'amélioration continue de vos performances logistiques.</p>
    
    <h2>🔧 Accès au Dashboard Incidents</h2>
    
    <h3>Navigation</h3>
    <ol>
      <li>Connectez-vous à Baobab Connect</li>
      <li>Cliquez sur <strong>"Dashboard"</strong> dans le menu principal</li>
      <li>Sélectionnez l'onglet <strong>"🚨 Incidents & Retards"</strong></li>
    </ol>
    
    <h3>Vue d'ensemble du dashboard</h3>
    <p>Le dashboard incidents vous donne accès à :</p>
    <ul>
      <li>Statistiques en temps réel</li>
      <li>Alertes urgentes</li>
      <li>Graphiques de tendances</li>
      <li>Actions rapides</li>
    </ul>
    
    <h2>📊 Fonctionnalités principales</h2>
    
    <h3>1. Monitoring en Temps Réel</h3>
    <div className="monitoring-features">
      <ul>
        <li><strong>Incidents Aujourd'hui</strong> : Compteur d'incidents du jour</li>
        <li><strong>Incidents ce Mois</strong> : Statistiques mensuelles</li>
        <li><strong>Opérations en Retard</strong> : Opérations dépassant leur délai prévu</li>
        <li><strong>Taux de Ponctualité</strong> : Pourcentage d'opérations à l'heure</li>
      </ul>
    </div>
    
    <h3>2. Classification des Incidents</h3>
    <div className="incident-classification">
      <div className="severity-level high">
        <h4>🔴 Gravité Élevée</h4>
        <p>Pannes, accidents, annulations</p>
      </div>
      
      <div className="severity-level medium">
        <h4>🟡 Gravité Moyenne</h4>
        <p>Retards, contrôles, blocages</p>
      </div>
      
      <div className="severity-level low">
        <h4>🟢 Gravité Faible</h4>
        <p>Incidents mineurs</p>
      </div>
    </div>
    
    <h3>3. Types d'Incidents Prédéfinis</h3>
    <div className="incident-types">
      <ul>
        <li>Retard de chargement</li>
        <li>Retard de déchargement</li>
        <li>Problème d'accès au lieu de chargement</li>
        <li>Panne véhicule</li>
        <li>Contrôle routier prolongé</li>
        <li>Conditions météorologiques</li>
        <li>Blocage administratif</li>
        <li>Accident de circulation</li>
        <li>Problème de sécurité</li>
        <li>Annulation de dernière minute</li>
      </ul>
    </div>
    
    <h2>🚛 Processus de Clôture d'Opération</h2>
    
    <h3>Modal de Clôture Automatique</h3>
    <p>Quand vous changez le statut d'une opération à <strong>"Terminée"</strong>, un modal s'ouvre automatiquement :</p>
    
    <h4>Champs Obligatoires :</h4>
    <div className="closure-fields">
      <ol>
        <li><strong>Date de Départ Réelle</strong> : Date effective de départ</li>
        <li><strong>Heure de Départ Réelle</strong> : Heure effective de départ</li>
        <li><strong>Date d'Arrivée Réelle</strong> : Date effective d'arrivée</li>
        <li><strong>Heure d'Arrivée Réelle</strong> : Heure effective d'arrivée</li>
      </ol>
    </div>
    
    <h4>Section Incidents :</h4>
    <div className="incident-section">
      <ol start="5">
        <li><strong>Incidents Rencontrés</strong> : Oui/Non</li>
        <li><strong>Types d'Incidents</strong> : Sélection multiple (si applicable)</li>
        <li><strong>Compte-Rendu d'Incident</strong> : Description détaillée (si applicable)</li>
      </ol>
    </div>
    
    <h3>Processus étape par étape :</h3>
    <div className="step-by-step">
      <div className="step">
        <span className="step-number">1</span>
        <div className="step-content">
          <h4>Changement de statut</h4>
          <p>Changez le statut à "Terminée"</p>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">2</span>
        <div className="step-content">
          <h4>Modal automatique</h4>
          <p>Le modal s'ouvre automatiquement</p>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">3</span>
        <div className="step-content">
          <h4>Saisie des données</h4>
          <p>Remplissez tous les champs obligatoires</p>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">4</span>
        <div className="step-content">
          <h4>Gestion des incidents</h4>
          <p>Sélectionnez les incidents si applicable</p>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">5</span>
        <div className="step-content">
          <h4>Finalisation</h4>
          <p>Cliquez sur "Terminer l'Opération"</p>
        </div>
      </div>
    </div>
    
    <h2>📝 Signalement d'Incident</h2>
    
    <h3>Procédure de signalement</h3>
    <ol>
      <li>Dans le modal de clôture, cochez <strong>"Oui"</strong> pour "Incidents Rencontrés"</li>
      <li>Sélectionnez un ou plusieurs <strong>types d'incidents</strong></li>
      <li>Rédigez un <strong>compte-rendu détaillé</strong></li>
      <li>Validez la clôture</li>
    </ol>
    
    <h3>Suivi des Incidents</h3>
    <ul>
      <li><strong>Dashboard Principal</strong> : Vue d'ensemble des incidents</li>
      <li><strong>Graphiques par Type</strong> : Répartition des incidents</li>
      <li><strong>Temps de Résolution</strong> : Durée moyenne de résolution</li>
      <li><strong>Routes Critiques</strong> : Routes avec le plus d'incidents</li>
    </ul>
    
    <h3>Alertes Automatiques</h3>
    <div className="alerts-system">
      <ul>
        <li><strong>Seuil d'Alerte</strong> : Plus de 2 incidents par jour</li>
        <li><strong>Notification Visuelle</strong> : Badge rouge clignotant</li>
        <li><strong>Actions Suggérées</strong> : Intervention recommandée</li>
      </ul>
    </div>
    
    <h2>📊 Analyse et Rapports</h2>
    
    <h3>Métriques Disponibles</h3>
    <div className="metrics">
      <ol>
        <li><strong>Taux de Ponctualité</strong> : Pourcentage d'opérations à l'heure</li>
        <li><strong>Temps de Livraison Moyen</strong> : En jours</li>
        <li><strong>Incidents par Type</strong> : Graphique en camembert</li>
        <li><strong>Tendance Mensuelle</strong> : Évolution des incidents</li>
        <li><strong>Performance Transporteurs</strong> : Score global avec incidents</li>
      </ol>
    </div>
    
    <h3>Routes Critiques</h3>
    <p>Le système identifie automatiquement :</p>
    <div className="critical-routes">
      <ul>
        <li>Routes avec taux d'incidents &gt; 30% (risque élevé)</li>
        <li>Routes avec taux d'incidents &gt; 15% (risque moyen)</li>
        <li>Recommandations d'amélioration</li>
      </ul>
    </div>
    
    <h3>Actions Recommandées</h3>
    <div className="recommended-actions">
      <ul>
        <li><strong>Formation Conducteurs</strong> : Pour réduire les incidents</li>
        <li><strong>Optimisation Routes</strong> : Pour éviter les zones à risque</li>
        <li><strong>Maintenance Préventive</strong> : Pour réduire les pannes</li>
      </ul>
    </div>
    
    <h2>🎯 Actions Rapides</h2>
    
    <p>Dans le dashboard, utilisez les boutons d'action rapide :</p>
    
    <div className="quick-actions">
      <div className="action-item">
        <h4>🚨 Signaler Incident</h4>
        <p>Déclarer un incident immédiatement</p>
      </div>
      
      <div className="action-item">
        <h4>⏰ Gérer Retards</h4>
        <p>Voir toutes les opérations en retard</p>
      </div>
      
      <div className="action-item">
        <h4>📊 Historique</h4>
        <p>Consulter l'historique des incidents</p>
      </div>
      
      <div className="action-item">
        <h4>🛡️ Plan Prévention</h4>
        <p>Accéder aux mesures préventives</p>
      </div>
    </div>
    
    <h2>⚙️ Configuration</h2>
    
    <h3>Seuils d'Alerte (Configurables par Admin)</h3>
    <ul>
      <li>Nombre d'incidents/jour déclenchant l'alerte</li>
      <li>Taux de retard acceptable</li>
      <li>Types d'incidents prioritaires</li>
    </ul>
    
    <h3>Notifications</h3>
    <ul>
      <li>Alertes automatiques pour incidents critiques</li>
      <li>Rapports hebdomadaires par email</li>
      <li>Notifications push pour retards importants</li>
    </ul>
    
    <div className="version-info">
      <h3>🔄 Informations de version</h3>
      <p><strong>Version</strong> : 2.1.0<br/>
      <strong>Date</strong> : Juillet 2025<br/>
      <strong>Nouvelles Fonctionnalités</strong> :</p>
      <ul>
        <li>Suivi incidents en temps réel</li>
        <li>Dashboard dédié incidents & retards</li>
        <li>Calculs automatiques de pénalités</li>
        <li>Routes critiques automatisées</li>
      </ul>
    </div>
  </div>
);
const TrackingContent = () => <DefaultContent />;
// Pénalités d'immobilisation - intégré depuis GUIDE_PENALITES_IMMOBILISATION.md
const PenaltiesContent = () => (
  <div className="help-content">
    <h1>💰 Pénalités d'immobilisation</h1>
    
    <h2>🎯 Vue d'ensemble</h2>
    <p>Le système de pénalités d'immobilisation permet de facturer automatiquement les frais de détention de camions et de gérer la répartition entre client et transporteur.</p>
    
    <h3>Fonctionnalités principales :</h3>
    <ul>
      <li>Facturer automatiquement les frais de détention de camions</li>
      <li>Calculer la part transporteur et client</li>
      <li>Intégrer les frais dans les documents officiels</li>
      <li>Suivre l'impact financier des retards</li>
    </ul>
    
    <h2>🔧 Configuration d'une Opération</h2>
    
    <h3>1. Activation des Pénalités</h3>
    <div className="activation-process">
      <p>Dans le formulaire <strong>"Nouvelle Opération"</strong> ou <strong>"Modifier Opération"</strong> :</p>
      
      <ol>
        <li>Recherchez la section <strong>"Options de Facturation"</strong></li>
        <li>Cochez la case <strong>"Facturer pénalités d'immobilisation"</strong></li>
        <li>Sauvegardez l'opération</li>
      </ol>
    </div>
    
    <h3>2. Indication Visuelle</h3>
    <p>Une fois activée, l'opération affichera un indicateur visuel :</p>
    <ul>
      <li>🚨 Icône d'alerte dans la liste des opérations</li>
      <li>Mention "Pénalités activées" dans les détails</li>
    </ul>
    
    <h2>📋 Création des Documents avec Pénalités</h2>
    
    <h3>1. Accès au Formulaire de Document</h3>
    <ol>
      <li>Depuis une opération avec pénalités activées</li>
      <li>Cliquez sur <strong>"Créer Document"</strong></li>
      <li>Choisissez le type : Devis, Bon de Commande, ou Facture</li>
    </ol>
    
    <h3>2. Section Frais d'Immobilisation</h3>
    <p>Dans le formulaire de document, localisez la section <strong>"Frais d'immobilisation"</strong> :</p>
    
    <h4>Champs à Remplir :</h4>
    <div className="form-fields">
      <table>
        <thead>
          <tr>
            <th>Champ</th>
            <th>Description</th>
            <th>Exemple</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Montant par Jour</strong></td>
            <td>Tarif journalier d'immobilisation</td>
            <td>100,000 FCFA</td>
          </tr>
          <tr>
            <td><strong>Nombre de Jours</strong></td>
            <td>Durée de l'immobilisation</td>
            <td>3 jours</td>
          </tr>
          <tr>
            <td><strong>Part Transporteur</strong></td>
            <td>Montant alloué au transporteur</td>
            <td>150,000 FCFA</td>
          </tr>
          <tr>
            <td><strong>Commentaire</strong></td>
            <td>Justification du retard</td>
            <td>"Retard déchargement - problème accès site"</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <h4>Exemple de Saisie :</h4>
    <div className="example-box">
      <pre><code>Montant par jour : 100,000 FCFA
Nombre de jours : 3
Part transporteur : 150,000 FCFA
Commentaire : "Retard de déchargement de 3 jours suite à problème d'accès au site"</code></pre>
    </div>
    
    <h3>3. Calcul Automatique</h3>
    <p>Le système calcule automatiquement :</p>
    <div className="calculation">
      <ul>
        <li><strong>Total Immobilisation</strong> = Montant/jour × Nombre de jours</li>
        <li><strong>Part Client</strong> = Total - Part Transporteur</li>
        <li><strong>Impact sur Totaux</strong> = Intégration dans le total TTC</li>
      </ul>
    </div>
    
    <h2>📄 Affichage dans les Documents</h2>
    
    <h3>Documents Client (Devis/Facture)</h3>
    <p>Les frais apparaissent comme une ligne séparée :</p>
    
    <div className="document-example">
      <h4>Exemple d'affichage :</h4>
      <div className="code-example">
        <pre><code>PRESTATIONS
- Transport marchandises : 2,500,000 FCFA
- Frais d'immobilisation camion : 3 jours × 100,000 FCFA = 300,000 FCFA
  Commentaire : Retard de déchargement de 3 jours suite à problème d'accès au site

TOTAL HT : 2,800,000 FCFA
TVA 18% : 504,000 FCFA
TOTAL TTC : 3,304,000 FCFA</code></pre>
      </div>
    </div>
    
    <h3>Bon de Commande Transporteur</h3>
    <p>Affiche uniquement la part transporteur :</p>
    
    <div className="document-example">
      <h4>Exemple d'affichage :</h4>
      <div className="code-example">
        <pre><code>PRESTATIONS TRANSPORTEUR
- Transport marchandises : 2,000,000 FCFA
- Frais d'immobilisation (part transporteur) : 150,000 FCFA

TOTAL À PAYER : 2,150,000 FCFA</code></pre>
      </div>
    </div>
    
    <h2>💡 Bonnes Pratiques</h2>
    
    <h3>1. Documentation des Retards</h3>
    <div className="best-practices">
      <ul>
        <li><strong>Soyez Précis</strong> : Documentez exactement la cause du retard</li>
        <li><strong>Photos/Preuves</strong> : Conservez les justificatifs si possible</li>
        <li><strong>Communication</strong> : Informez le client avant facturation</li>
      </ul>
    </div>
    
    <h3>2. Calcul des Montants</h3>
    <div className="amount-calculation">
      <ul>
        <li><strong>Tarifs Standard</strong> : Définissez des tarifs par type de camion</li>
        <li><strong>Part Transporteur</strong> : Généralement 50% du montant total</li>
        <li><strong>Négociation</strong> : Adaptez selon les accords client</li>
      </ul>
    </div>
    
    <h3>3. Validation Interne</h3>
    <div className="validation-process">
      <ul>
        <li><strong>Vérification</strong> : Validez les montants avant émission</li>
        <li><strong>Approbation</strong> : Faites valider par un superviseur si montant élevé</li>
        <li><strong>Suivi</strong> : Surveillez l'impact sur la relation client</li>
      </ul>
    </div>
    
    <h2>📊 Suivi et Analyse</h2>
    
    <h3>1. Dashboard Incidents</h3>
    <p>Dans le dashboard <strong>"Incidents & Retards"</strong> :</p>
    <ul>
      <li>Visualisez l'impact financier des retards</li>
      <li>Identifiez les routes problématiques</li>
      <li>Suivez les tendances mensuelles</li>
    </ul>
    
    <h3>2. Rapports Financiers</h3>
    <ul>
      <li><strong>Revenus Additionnels</strong> : Montant généré par les pénalités</li>
      <li><strong>Performance Transporteurs</strong> : Impact des retards sur les coûts</li>
      <li><strong>Analyse Client</strong> : Clients les plus impactés</li>
    </ul>
    
    <h2>Gestion des Litiges</h2>
    
    <h3>1. Prévention</h3>
    <ul>
      <li><strong>Communication Proactive</strong> : Informez en cas de retard prévisible</li>
      <li><strong>Documentation</strong> : Conservez toutes les preuves</li>
      <li><strong>Transparence</strong> : Expliquez clairement les frais</li>
    </ul>
    
    <h3>2. Résolution</h3>
    <ul>
      <li><strong>Négociation</strong> : Soyez flexible selon les circonstances</li>
      <li><strong>Geste Commercial</strong> : Réduisez si le client n'est pas responsable</li>
      <li><strong>Apprentissage</strong> : Utilisez pour améliorer les processus</li>
    </ul>
    
    <h2>🔧 Configuration Avancée</h2>
    
    <h3>1. Tarifs par Type de Camion</h3>
    <div className="truck-rates">
      <table>
        <thead>
          <tr>
            <th>Type de Camion</th>
            <th>Tarif Journalier</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Camion 19T</td>
            <td>80,000 FCFA/jour</td>
          </tr>
          <tr>
            <td>Camion 38T</td>
            <td>120,000 FCFA/jour</td>
          </tr>
          <tr>
            <td>Semi-remorque</td>
            <td>150,000 FCFA/jour</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <h3>2. Règles de Répartition</h3>
    <div className="distribution-rules">
      <table>
        <thead>
          <tr>
            <th>Responsabilité</th>
            <th>Répartition Client</th>
            <th>Répartition Transporteur</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Client Responsable</td>
            <td>100%</td>
            <td>0%</td>
          </tr>
          <tr>
            <td>Transporteur Responsable</td>
            <td>0%</td>
            <td>100%</td>
          </tr>
          <tr>
            <td>Cause Externe</td>
            <td>50%</td>
            <td>50%</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <h2>📞 Exemples Concrets</h2>
    
    <h3>Exemple 1 : Retard de Déchargement Client</h3>
    <div className="example-scenario">
      <p><strong>Situation</strong> : Client ne libère pas le camion pendant 2 jours</p>
      <p><strong>Calcul</strong> : 2 × 100,000 = 200,000 FCFA</p>
      <p><strong>Répartition</strong> : 200,000 FCFA client, 0 FCFA transporteur</p>
    </div>
    
    <h3>Exemple 2 : Panne Transporteur</h3>
    <div className="example-scenario">
      <p><strong>Situation</strong> : Panne mécanique immobilise le camion 1 jour</p>
      <p><strong>Calcul</strong> : 1 × 100,000 = 100,000 FCFA</p>
      <p><strong>Répartition</strong> : 0 FCFA client, 100,000 FCFA transporteur</p>
    </div>
    
    <h3>Exemple 3 : Cause Externe (Grève)</h3>
    <div className="example-scenario">
      <p><strong>Situation</strong> : Blocage routier pour grève, 3 jours</p>
      <p><strong>Calcul</strong> : 3 × 100,000 = 300,000 FCFA</p>
      <p><strong>Répartition</strong> : 150,000 FCFA client, 150,000 FCFA transporteur</p>
    </div>
    
    <h2>📈 ROI et Bénéfices</h2>
    
    <h3>1. Réduction des Retards</h3>
    <div className="benefits">
      <ul>
        <li><strong>Responsabilisation</strong> : Les frais incitent à la ponctualité</li>
        <li><strong>Compensation</strong> : Couvre les coûts d'immobilisation</li>
        <li><strong>Amélioration</strong> : Processus plus efficaces</li>
      </ul>
    </div>
    
    <h3>2. Impact Financier</h3>
    <div className="financial-impact">
      <ul>
        <li><strong>Revenus Additionnels</strong> : 5-10% d'augmentation du CA</li>
        <li><strong>Couverture Coûts</strong> : Compensation des frais fixes</li>
        <li><strong>Compétitivité</strong> : Tarifs plus justes et transparents</li>
      </ul>
    </div>
  </div>
);
const DocumentTypesContent = () => <DefaultContent />;
const PricingContent = () => <DefaultContent />;
const TemplatesContent = () => <DefaultContent />;
const PDFGenerationContent = () => <DefaultContent />;
const BonCommandeContent = () => <DefaultContent />;
const ClientsContent = () => <DefaultContent />;
const ContactsContent = () => <DefaultContent />;
const CompaniesContent = () => <DefaultContent />;
const ImportExportContent = () => <DefaultContent />;
const SegmentationContent = () => <DefaultContent />;
const TransporterManagementContent = () => <DefaultContent />;
const FleetContent = () => <DefaultContent />;
const DriversContent = () => <DefaultContent />;
const RatesContent = () => <DefaultContent />;
const StandardReportsContent = () => <DefaultContent />;
const CustomReportsContent = () => <DefaultContent />;
const ExportsContent = () => <DefaultContent />;
const AnalyticsContent = () => <DefaultContent />;
const UserManagementContent = () => <DefaultContent />;
const RolesPermissionsContent = () => <DefaultContent />;
const CompanySettingsContent = () => <DefaultContent />;
const SystemSettingsContent = () => <DefaultContent />;
// Sauvegarde et restauration - intégré depuis GUIDE_BACKUP_UPDATED.md
const BackupRestoreContent = () => (
  <div className="help-content">
    <h1>💾 Sauvegarde et restauration</h1>
    
    <h2>🔒 Vue d'ensemble</h2>
    <p>Le système de backup de Baobab Connect assure la protection et la récupération de vos données critiques avec un chiffrement de niveau bancaire.</p>
    
    <div className="version-info">
      <h3>Version actuelle</h3>
      <p><strong>Date de mise à jour :</strong> 2025-01-06<br/>
      <strong>Version des données :</strong> Post-migration pricing fixes</p>
    </div>
    
    <h2>✅ Nouvelles fonctionnalités intégrées</h2>
    
    <h3>Corrections récentes appliquées</h3>
    <div className="recent-fixes">
      <ol>
        <li><strong>Nouveau champ :</strong> <code>appliquer_retenue_forfaitaire</code> (contrôle indépendant de la retenue 2%)</li>
        <li><strong>Corrections décimales :</strong> Toutes les valeurs monétaires arrondies correctement</li>
        <li><strong>Recalculs pricing :</strong> Formules de marge corrigées (markup on cost)</li>
        <li><strong>Endpoints API :</strong> Correction <code>/api/societes</code> → <code>/api/crm/clients</code></li>
      </ol>
    </div>
    
    <h3>📊 Statistiques de Migration (06/01/2025)</h3>
    <div className="migration-stats">
      <table>
        <thead>
          <tr>
            <th>Type de correction</th>
            <th>Nombre d'éléments</th>
            <th>Statut</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Documents traités</td>
            <td>82/91</td>
            <td>✅ Réussi</td>
          </tr>
          <tr>
            <td>Corrections décimales</td>
            <td>17 documents</td>
            <td>✅ Corrigé</td>
          </tr>
          <tr>
            <td>Champs ajoutés</td>
            <td>81 documents</td>
            <td>✅ Ajouté</td>
          </tr>
          <tr>
            <td>Recalculs pricing</td>
            <td>11 documents</td>
            <td>✅ Recalculé</td>
          </tr>
          <tr>
            <td>Erreurs</td>
            <td>0</td>
            <td>✅ Aucune</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <h2>🛠️ Commandes de Backup</h2>
    
    <h3>Backup Manuel</h3>
    <div className="command-section">
      <h4>Créer un backup immédiat</h4>
      <div className="code-example">
        <pre><code>/app/scripts/secure_backup.sh create</code></pre>
      </div>
      
      <h4>Lister les backups disponibles</h4>
      <div className="code-example">
        <pre><code>/app/scripts/secure_backup.sh list</code></pre>
      </div>
      
      <h4>Vérifier l'intégrité d'un backup</h4>
      <div className="code-example">
        <pre><code>/app/scripts/secure_backup.sh verify /backup/baobab-connect/baobab_backup_YYYYMMDD_HHMMSS.tar.gz.enc</code></pre>
      </div>
    </div>
    
    <h3>Restauration</h3>
    <div className="restore-warning">
      <h4>ATTENTION : Écrase les données actuelles</h4>
      <div className="code-example">
        <pre><code>/app/scripts/secure_backup.sh restore /backup/baobab-connect/baobab_backup_YYYYMMDD_HHMMSS.tar.gz.enc</code></pre>
      </div>
    </div>
    
    <h2>🤖 Backup Automatisé</h2>
    
    <p>Le système crée automatiquement des backups selon ce planning :</p>
    
    <div className="schedule">
      <table>
        <thead>
          <tr>
            <th>Fréquence</th>
            <th>Heure</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Quotidien</td>
            <td>2h00 du matin</td>
            <td>Backup complet automatique</td>
          </tr>
          <tr>
            <td>Hebdomadaire</td>
            <td>Dimanche 3h00</td>
            <td>Nettoyage (supprime backups &gt; 30 jours)</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <h3>Logs de surveillance</h3>
    <p><strong>Fichier de logs :</strong> <code>/var/log/baobab-backup.log</code></p>
    
    <h2>🔐 Sécurité</h2>
    
    <h3>Clé de Chiffrement</h3>
    <div className="security-details">
      <table>
        <thead>
          <tr>
            <th>Paramètre</th>
            <th>Valeur</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Emplacement</td>
            <td><code>/etc/baobab/backup.key</code></td>
          </tr>
          <tr>
            <td>Algorithme</td>
            <td>AES-256-CBC</td>
          </tr>
          <tr>
            <td>Permissions</td>
            <td>-rw------- (600)</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <div className="critical-warning">
      <h3>CRITIQUE : Sauvegarde de la clé</h3>
      <p><strong>IMPORTANT :</strong> Sauvegardez cette clé séparément - sans elle, les backups sont inutilisables !</p>
    </div>
    
    <h3>Permissions système</h3>
    <ul>
      <li><strong>Backups :</strong> <code>-rw------- (600)</code> - Lecture/écriture root uniquement</li>
      <li><strong>Clé :</strong> <code>-rw------- (600)</code> - Lecture/écriture root uniquement</li>
    </ul>
    
    <h2>📁 Structure des Backups</h2>
    
    <div className="backup-structure">
      <h3>Organisation des fichiers</h3>
      <div className="code-example">
        <pre><code>/backup/baobab-connect/
├── baobab_backup_20250806_004243.tar.gz.enc  ← Backup actuel (post-migration)
├── baobab_backup_YYYYMMDD_HHMMSS.tar.gz.enc  ← Autres backups
└── [anciens backups selon rétention]</code></pre>
      </div>
    </div>
    
    <h2>🆘 Procédure de Restauration d'Urgence</h2>
    
    <div className="emergency-procedure">
      <div className="step">
        <span className="step-number">1</span>
        <div className="step-content">
          <h4>Identifier le backup à restaurer</h4>
          <div className="code-example">
            <pre><code>/app/scripts/secure_backup.sh list</code></pre>
          </div>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">2</span>
        <div className="step-content">
          <h4>Vérifier l'intégrité</h4>
          <div className="code-example">
            <pre><code>/app/scripts/secure_backup.sh verify [fichier_backup]</code></pre>
          </div>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">3</span>
        <div className="step-content">
          <h4>ARRÊTER les services</h4>
          <div className="code-example">
            <pre><code>sudo supervisorctl stop backend</code></pre>
          </div>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">4</span>
        <div className="step-content">
          <h4>Restaurer</h4>
          <div className="code-example">
            <pre><code>/app/scripts/secure_backup.sh restore [fichier_backup]</code></pre>
          </div>
        </div>
      </div>
      
      <div className="step">
        <span className="step-number">5</span>
        <div className="step-content">
          <h4>Redémarrer les services</h4>
          <div className="code-example">
            <pre><code>sudo supervisorctl start backend</code></pre>
          </div>
        </div>
      </div>
    </div>
    
    <h2>🔍 Vérification Post-Restauration</h2>
    
    <p>Après une restauration, vérifiez que :</p>
    
    <h3>1. Les nouveaux champs existent</h3>
    <div className="verification-step">
      <h4>Via API</h4>
      <div className="code-example">
        <pre><code>curl -H "Authorization: Bearer [token]" http://localhost:8001/api/documents?limit=1

# Vérifier: appliquer_retenue_forfaitaire présent</code></pre>
      </div>
    </div>
    
    <h3>2. Les calculs sont corrects</h3>
    <div className="verification-checklist">
      <ul>
        <li>Prix client HT arrondis (pas de décimales aberrantes)</li>
        <li>Formules de marge appliquées correctement</li>
        <li>Totaux cohérents</li>
      </ul>
    </div>
    
    <h3>3. Les endpoints fonctionnent</h3>
    <div className="endpoint-checks">
      <ul>
        <li><code>/api/crm/clients</code> retourne les sociétés</li>
        <li>Les listes déroulantes se chargent dans l'interface</li>
      </ul>
    </div>
    
    <h2>📈 Monitoring des Backups</h2>
    
    <h3>Vérification de l'état des backups</h3>
    <div className="monitoring-commands">
      <h4>Vérifier les logs</h4>
      <div className="code-example">
        <pre><code>tail -f /var/log/baobab-backup.log</code></pre>
      </div>
      
      <h4>Tester manuellement</h4>
      <div className="code-example">
        <pre><code>/app/scripts/secure_backup.sh create</code></pre>
      </div>
      
      <h4>Vérifier l'espace disque</h4>
      <div className="code-example">
        <pre><code>df -h /backup/</code></pre>
      </div>
    </div>
    
    <h3>Alertes Recommandées</h3>
    <div className="alert-recommendations">
      <ul>
        <li>Backup quotidien non créé &gt; 25h</li>
        <li>Espace disque &lt; 1GB sur /backup/</li>
        <li>Échec de vérification d'intégrité</li>
      </ul>
    </div>
    
    <h2>🎯 Bonnes Pratiques</h2>
    
    <div className="best-practices">
      <ol>
        <li><strong>Testez régulièrement</strong> la restauration sur un environnement de test</li>
        <li><strong>Sauvegardez la clé de chiffrement</strong> dans un endroit sécurisé séparé</li>
        <li><strong>Surveillez l'espace disque</strong> dans /backup/</li>
        <li><strong>Documentez les restaurations</strong> pour audit</li>
        <li><strong>Vérifiez l'intégrité</strong> des backups critiques</li>
      </ol>
    </div>
    
    <h2>📞 En cas de problème</h2>
    
    <p>Si vous rencontrez des problèmes avec les backups :</p>
    
    <div className="troubleshooting">
      <ol>
        <li>Vérifiez les logs : <code>tail /var/log/baobab-backup.log</code></li>
        <li>Testez manuellement : <code>/app/scripts/secure_backup.sh create</code></li>
        <li>Vérifiez les permissions sur <code>/backup/</code> et <code>/etc/baobab/</code></li>
        <li>Assurez-vous que MongoDB est accessible</li>
      </ol>
    </div>
    
    <div className="critical-note">
      <h3>ATTENTION CRITIQUE</h3>
      <p><strong>Ne jamais supprimer <code>/etc/baobab/backup.key</code> sans avoir une copie sécurisée !</strong></p>
    </div>
  </div>
);
const MonitoringContent = () => <DefaultContent />;
const APIAuthenticationContent = () => <DefaultContent />;
const APIEndpointsContent = () => <DefaultContent />;
const RateLimitingContent = () => <DefaultContent />;
const WebhooksContent = () => <DefaultContent />;
const SDKContent = () => <DefaultContent />;
const SecurityAuthContent = () => <DefaultContent />;
const AuthorizationContent = () => <DefaultContent />;
const DataProtectionContent = () => <DefaultContent />;
const AuditLogsContent = () => <DefaultContent />;
const BestPracticesContent = () => <DefaultContent />;
const ThirdPartyContent = () => <DefaultContent />;
const WebhookConfigContent = () => <DefaultContent />;
const APIKeysContent = () => <DefaultContent />;
const ExternalSystemsContent = () => <DefaultContent />;
// Questions fréquentes - intégré depuis FAQ_BAOBAB_CONNECT.md
const CommonIssuesContent = () => (
  <div className="help-content">
    <h1>❓ Questions fréquentes</h1>
    
    <h2>💰 Gestion des Paiements</h2>
    
    <h3>Q: Pourquoi n'y a-t-il pas de bouton paiement sur les devis ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> C'est normal ! Les devis sont uniquement des documents commerciaux de négociation. La gestion des paiements n'est disponible que pour :</p>
      <ul>
        <li><strong>Factures :</strong> Encaissement client</li>
        <li><strong>Bons de commande :</strong> Paiement transporteur</li>
      </ul>
    </div>
    
    <h3>Q: Quelle est la différence entre "encaissement" et "paiement" ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong></p>
      <div className="terminology-section">
        <h4>📋 Factures (Terminologie "encaissement")</h4>
        <p>Argent reçu des clients</p>
        <ul>
          <li>"Montant déjà encaissé", "Reste à encaisser"</li>
          <li>Statuts : "encaissé", "partiellement_encaissé"</li>
        </ul>
        
        <h4>🚛 Bons de commande (Terminologie "paiement")</h4>
        <p>Argent versé aux transporteurs</p>
        <ul>
          <li>"Montant déjà payé", "Reste à payer"</li>
          <li>Statuts : "payé", "partiellement_payé"</li>
        </ul>
      </div>
    </div>
    
    <h3>Q: Qui apparaît comme "entité" sur les documents de paiement ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong></p>
      <ul>
        <li><strong>Factures :</strong> "Client" (celui qui doit payer)</li>
        <li><strong>Bons de commande :</strong> "Prestataire" (transporteur à payer)</li>
      </ul>
    </div>
    
    <h3>Q: Quelle est la condition de paiement sur les factures ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> "Le paiement est exigible dans un délai de dix (10) jours calendaires à compter de la date de dépôt de la facture."</p>
    </div>
    
    <h3>Q: Comment lier un bon de commande à un devis ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Dans la création d'un bon de commande :</p>
      <ol>
        <li>Section "Liaison avec un devis"</li>
        <li>Sélectionnez un devis "accepté" ou "validé"</li>
        <li>Les informations se remplissent automatiquement (client, items, totaux)</li>
      </ol>
    </div>
    
    <h2>👥 Gestion des Sociétés</h2>
    
    <h3>Q: Quels sont les nouveaux champs disponibles pour les sociétés ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Trois nouveaux champs principaux :</p>
      <ul>
        <li><strong>Type de contact :</strong> Client potentiel (défaut), Client actuel, Autre</li>
        <li><strong>Pays :</strong> Côte d'Ivoire par défaut, modifiable</li>
        <li><strong>Ville :</strong> Localisation précise (Abidjan, Bouaké, etc.)</li>
      </ul>
    </div>
    
    <h3>Q: Comment filtrer les sociétés par type de contact ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Dans le module Sociétés, utilisez le dropdown "Type de contact" dans la barre de filtres (8 critères disponibles).</p>
    </div>
    
    <h3>Q: Comment exporter les sociétés en CSV ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong></p>
      <ol>
        <li>Dans le module Sociétés, appliquez vos filtres si nécessaire</li>
        <li>Cliquez sur l'icône 📄 dans la barre d'outils</li>
        <li>Le fichier CSV se télécharge avec 17 colonnes incluant les nouveaux champs</li>
        <li>Format : <code>societes_baobab_connect_YYYYMMDD_HHMMSS.csv</code></li>
      </ol>
    </div>
    
    <h3>Q: L'export CSV respecte-t-il les filtres appliqués ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Oui ! Si vous filtrez par ville, type de contact ou autre critère, l'export ne contiendra que les sociétés correspondantes.</p>
    </div>
    
    <h2>🔐 Connexion & Comptes</h2>
    
    <h3>Q: J'ai oublié mon mot de passe, que faire ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Contactez votre administrateur système pour réinitialiser votre mot de passe. Un nouveau mot de passe temporaire vous sera fourni.</p>
    </div>
    
    <h3>Q: Combien d'utilisateurs peuvent se connecter simultanément ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Baobab Connect supporte plusieurs utilisateurs simultanés. Le nombre dépend de votre licence.</p>
    </div>
    
    <h3>Q: Puis-je changer mon mot de passe ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Oui, rendez-vous dans "Paramètres > Mon Profil" pour modifier votre mot de passe.</p>
    </div>
    
    <h2>📊 Dashboard & Métriques</h2>
    
    <h3>Q: Combien de dashboards sont disponibles ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Baobab Connect propose 8 dashboards spécialisés :</p>
      <ul>
        <li><strong>Vue d'ensemble :</strong> KPIs généraux</li>
        <li><strong>Finance :</strong> Séparation encaissement client vs paiement transporteur</li>
        <li><strong>Opérations :</strong> Suivi logistique avec infos transporteur</li>
        <li><strong>Incidents & Retards :</strong> Gestion des problèmes</li>
        <li><strong>Objectifs :</strong> Progression vers cibles</li>
        <li><strong>Vue Classique :</strong> Interface traditionnelle</li>
        <li><strong>Performance :</strong> Analyses avec scores globaux</li>
        <li><strong>Pipeline :</strong> Prévisions commerciales</li>
      </ul>
    </div>
    
    <h3>Q: Les chiffres du dashboard ne se mettent pas à jour, pourquoi ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Les métriques sont actualisées en temps réel. Vous pouvez rafraîchir la page (F5) ou naviguer entre les onglets pour forcer la mise à jour.</p>
    </div>
    
    <h3>Q: Comment interpréter la "Marge Moyenne" ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> C'est le pourcentage moyen de marge bénéficiaire sur toutes vos opérations.</p>
      <p><strong>Formule :</strong> (Total HT - Tarif Transporteur) / Total HT × 100</p>
    </div>
    
    <h2>🎯 Opportunités</h2>
    
    <h3>Q: Quelle est la différence entre "Prospection" et "Négociation" ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong></p>
      <ul>
        <li><strong>Prospection :</strong> Contact initial, identification du besoin</li>
        <li><strong>Négociation :</strong> Discussion active des conditions et tarifs</li>
      </ul>
    </div>
    
    <h3>Q: Comment passer une opportunité de "Négociation" à "Gagnée" ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Cliquez sur l'opportunité → Modifier → Statut → Gagnée. Vous pourrez ensuite créer l'opération correspondante.</p>
    </div>
    
    <h3>Q: Puis-je récupérer une opportunité "Perdue" ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Oui, modifiez le statut et remettez-la en "Négociation" si le client change d'avis.</p>
    </div>
    
    <h2>🚛 Opérations</h2>
    
    <h3>Q: Comment choisir le bon type de camion ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Baobab Connect propose 100+ types spécifiques au marché ivoirien :</p>
      <ul>
        <li><strong>Transport urbain :</strong> Camions 3.5T, 7.5T, 12T</li>
        <li><strong>Port d'Abidjan :</strong> Porte-conteneur 20', 40'</li>
        <li><strong>Agriculture :</strong> Transport cacao, café, hévéa</li>
        <li><strong>BTP :</strong> Benne TP, malaxeur béton</li>
      </ul>
    </div>
    
    <h3>Q: Puis-je modifier une opération en cours ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Oui, tant qu'elle n'est pas en statut "Terminée". Attention aux implications contractuelles.</p>
    </div>
    
    <h3>Q: Comment calculer le coût d'une opération ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Utilisez le formulaire de tarification intégré :</p>
      <ol>
        <li>Tarif transporteur HT (base)</li>
        <li>+ Marge (20% par défaut)</li>
        <li>+ TVA (18%)</li>
        <li>= Prix final client TTC</li>
      </ol>
    </div>
    
    <h2>📄 Documents</h2>
    
    <h3>Q: Quelle est la logique Devis → Bon de Commande → Facture ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong></p>
      <ul>
        <li><strong>Devis :</strong> Proposition commerciale (validité 30j)</li>
        <li><strong>Bon de Commande :</strong> Confirmation client (engagement)</li>
        <li><strong>Facture :</strong> Demande de paiement (après livraison)</li>
      </ul>
    </div>
    
    <h3>Q: Comment personnaliser mes documents ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Deux templates disponibles :</p>
      <ul>
        <li><strong>Standard :</strong> Mise en page classique</li>
        <li><strong>Avancé :</strong> Personnalisation graphique</li>
      </ul>
    </div>
    
    <h3>Q: Puis-je modifier un document après génération ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Oui, les documents restent modifiables. La régénération PDF prendra en compte les modifications.</p>
    </div>
    
    <h2>💰 Tarification</h2>
    
    <h3>Q: Comment fonctionne le calcul de marge ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong></p>
      <div className="calculation-example">
        <ul>
          <li><strong>Tarif Transporteur :</strong> 1,000,000 FCFA</li>
          <li><strong>Marge 20% :</strong> 200,000 FCFA</li>
          <li><strong>Total HT :</strong> 1,200,000 FCFA</li>
          <li><strong>TVA 18% :</strong> 216,000 FCFA</li>
          <li><strong>Total TTC :</strong> 1,416,000 FCFA</li>
        </ul>
      </div>
    </div>
    
    <h3>Q: Puis-je modifier le taux de TVA ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Oui, le taux peut être ajusté par document selon les spécificités fiscales.</p>
    </div>
    
    <h3>Q: Comment justifier ma marge client ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> La marge couvre :</p>
      <ul>
        <li>Gestion administrative</li>
        <li>Assurance responsabilité</li>
        <li>Services douane/transit</li>
        <li>Suivi opérationnel</li>
        <li>Garantie qualité service</li>
      </ul>
    </div>
    
    <h2>🛠️ Fonctions Techniques</h2>
    
    <h3>Q: L'application fonctionne-t-elle sur mobile ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Oui, interface 100% responsive. Compatible smartphones et tablettes.</p>
    </div>
    
    <h3>Q: Comment faire une sauvegarde de mes données ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Les données sont sauvegardées automatiquement. Export possible via "Export Center".</p>
    </div>
    
    <h3>Q: Puis-je utiliser Baobab Connect hors ligne ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Non, connexion internet requise. Les données sont synchronisées en temps réel.</p>
    </div>
    
    <h2>⚡ Performance</h2>
    
    <h3>Q: L'application est lente, que faire ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong></p>
      <ol>
        <li>Vérifiez votre connexion internet</li>
        <li>Rafraîchissez la page (F5)</li>
        <li>Videz le cache navigateur</li>
        <li>Contactez le support si problème persiste</li>
      </ol>
    </div>
    
    <h3>Q: Combien de documents puis-je stocker ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Pas de limite technique. Performances optimales jusqu'à plusieurs milliers de documents.</p>
    </div>
    
    <h2>🆘 Problèmes Courants</h2>
    
    <h3>Q: "Erreur 500" lors d'une action</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Erreur serveur temporaire. Rafraîchissez et réessayez. Contactez le support si récurrent.</p>
    </div>
    
    <h3>Q: "Session expirée" message</h3>
    <div className="faq-answer">
      <p><strong>R:</strong> Reconnectez-vous. Les sessions expirent après 24h d'inactivité.</p>
    </div>
    
    <h3>Q: Document PDF ne se génère pas</h3>
    <div className="faq-answer">
      <p><strong>R:</strong></p>
      <ol>
        <li>Vérifiez que tous les champs obligatoires sont remplis</li>
        <li>Essayez avec un autre navigateur</li>
        <li>Contactez le support avec détails du document</li>
      </ol>
    </div>
    
    <h3>Q: Import CSV échoue</h3>
    <div className="faq-answer">
      <p><strong>R:</strong></p>
      <ol>
        <li>Vérifiez le format de fichier (.csv)</li>
        <li>Encodage UTF-8 recommandé</li>
        <li>Colonnes obligatoires présentes</li>
        <li>Données valides (pas de champs vides critiques)</li>
      </ol>
    </div>
    
    <h2>📱 Compatibilité</h2>
    
    <h3>Q: Navigateurs recommandés ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong></p>
      <ul>
        <li>✅ <strong>Chrome</strong> (recommandé)</li>
        <li>✅ <strong>Firefox</strong></li>
        <li>✅ <strong>Safari</strong></li>
        <li><strong>Edge</strong> (versions récentes)</li>
        <li>❌ Internet Explorer (non supporté)</li>
      </ul>
    </div>
    
    <h3>Q: Configuration minimale ?</h3>
    <div className="faq-answer">
      <p><strong>R:</strong></p>
      <ul>
        <li>Connexion internet stable</li>
        <li>Navigateur récent (moins de 2 ans)</li>
        <li>Résolution 1024x768 minimum</li>
        <li>JavaScript activé</li>
      </ul>
    </div>
    
    <h2>📞 Contact Support</h2>
    
    <div className="support-section">
      <h3>🆘 Problème Urgent</h3>
      <ul>
        <li><strong>Email :</strong> support@baobabco.net</li>
        <li><strong>Téléphone :</strong> +225 05 00 16 37 37</li>
        <li><strong>Heures :</strong> Lundi-Vendredi, 8h00-18h00 GMT</li>
      </ul>
      
      <h3>🎓 Formation & Aide</h3>
      <ul>
        <li><strong>Documentation :</strong> docs.baobabconnect.com</li>
        <li><strong>Formations :</strong> formation@baobabco.net</li>
      </ul>
    </div>
    
    <div className="faq-footer">
      <h3>🌟 Cette FAQ ne répond pas à votre question ?</h3>
      <p>Contactez-nous, nous serons ravis de vous aider !</p>
    </div>
  </div>
);
const ErrorCodesContent = () => <DefaultContent />;
const PerformanceContent = () => <DefaultContent />;
const ContactSupportContent = () => <DefaultContent />;

export default ComprehensiveDocumentation;